import React, { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrash,
  faFileExcel,
  faFileImport,
  faTimes,
  faSkull,
  faArrowRight,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import "./ControlAves.css";
import { useShoppingContext } from "../../../context/ShoppingContext";

const ControlAves = () => {
  const {
    controlAves,
    setControlAves,
    setShowAddControlAves,
    fetchMisAves,
    fetchControlAves,
    getAveNombre,
    dropzone,
    setDropzone,
    selectedFilterDate,
    selectedAve, // Asegurarse de tener selectedAve disponible
    setSelectedAve,
    renderDateTitle,
    setLoading,
  } = useShoppingContext();

  const [dragging, setDragging] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFile, setSelectedFile] = useState(null);

  const [startPage, setStartPage] = useState(1); // Control de paginación
  const itemsPerPage = 1;
  const pagesToShow = 5;

  // Seleccionar automáticamente el lote si hay solo uno
  useEffect(() => {
    fetchMisAves();
    fetchControlAves();
  }, [fetchControlAves, fetchMisAves]);

  // Agrupar los registros por fecha y lote
  const groupControlAvesByDateAndLote = (controlAves) => {
    const grouped = controlAves.reduce((acc, control) => {
      const key = `${control.fecha}_${control.ave_id}`; // Crear una clave única por fecha y lote
      if (!acc[key]) {
        acc[key] = {
          fecha: control.fecha,
          lote: control.ave_id,
          totalMuertas: 0,
          motivos: [],
        };
      }
      if (control.death === true || control.death === 1) {
        acc[key].totalMuertas += 1; // Sumar una ave muerta solo si death es true o 1
        acc[key].motivos.push(control.motivo_muerte || "Desconocido"); // Añadir el motivo de muerte o "Desconocido"
      }
      return acc;
    }, {});

    return Object.values(grouped);
  };

  const filteredControlAves = groupControlAvesByDateAndLote(controlAves).filter(
    (control) => {
      const matchesLote = selectedAve
        ? control.lote === selectedAve.id // Comprobar si el lote coincide con el selectedAve
        : true;
      const matchesFecha = selectedFilterDate
        ? renderDateTitle(control.fecha) === renderDateTitle(selectedFilterDate)
        : true;
      return matchesLote && matchesFecha;
    }
  );

  const sortedControlAves = filteredControlAves.sort(
    (a, b) => new Date(b.fecha) - new Date(a.fecha)
  );

  const totalPages = Math.ceil(sortedControlAves.length / itemsPerPage);

  const paginatedData = sortedControlAves.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNextPageGroup = () => {
    if (startPage + pagesToShow <= totalPages) {
      setStartPage(startPage + pagesToShow);
      setCurrentPage(startPage + pagesToShow);
    }
  };

  const handlePrevPageGroup = () => {
    if (startPage - pagesToShow >= 1) {
      setStartPage(startPage - pagesToShow);
      setCurrentPage(startPage - pagesToShow);
    }
  };

  const renderPagination = () => {
    const pageNumbers = [];
    const endPage = Math.min(startPage + pagesToShow - 1, totalPages);
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <div className="pagination-container">
          <span
            className={`pagination-arrow ${
              currentPage === 1 ? "disabled" : ""
            }`}
            onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
          >
            {"<"}
          </span>
          {[...Array(totalPages)].map((_, index) => (
            <span
              key={index}
              className={`pagination-item ${
                currentPage === index + 1 ? "active" : ""
              }`}
              onClick={() => handlePageChange(index + 1)}
            >
              {index + 1}
            </span>
          ))}
          <span
            className={`pagination-arrow ${
              currentPage === totalPages ? "disabled" : ""
            }`}
            onClick={() =>
              currentPage < totalPages && handlePageChange(currentPage + 1)
            }
          >
            {">"}
          </span>
          <span className="pagination-info">
            Página {currentPage} de {totalPages}
          </span>
        </div>
      );
    }
    return pageNumbers;
  };

  const handleEditControlAve = (id) => {
    console.log(
      "Implementar la lógica para editar un registro de control de aves existente con ID:",
      id
    );
  };

  const handleDeleteAll = () => {
    if (
      window.confirm(
        "¿Estás seguro de que deseas eliminar todos los registros de control de aves?"
      )
    ) {
      axios
        .delete(`${process.env.REACT_APP_API_URL}/control-aves`)
        .then(() => {
          setControlAves([]);
          console.log(
            "Todos los registros de control de aves han sido eliminados"
          );
        })
        .catch((error) => {
          console.error(
            "Error al eliminar todos los registros de control de aves:",
            error
          );
        });
    }
  };

  const handleDelete = (fecha, lote) => {
    const formattedDate = renderDateTitle(fecha);

    if (
      window.confirm(
        `¿Está seguro de que desea eliminar todos los registros de mortalidad para el lote ${getAveNombre(
          lote
        )} en la fecha ${formattedDate}?`
      )
    ) {
      axios
        .delete(
          `${process.env.REACT_APP_API_URL}/control-aves/${fecha}/${lote}`
        )
        .then(() => {
          // Actualizar el saldo manualmente después de la eliminación
          setSelectedAve((prevAve) => ({
            ...prevAve,
            saldo: prevAve.saldo + prevAve.mortalidad, // Agregar el número de aves muertas de vuelta al saldo
          }));

          setControlAves((prevControlAves) =>
            prevControlAves.filter(
              (control) => !(control.fecha === fecha && control.ave_id === lote)
            )
          );
          fetchMisAves();
        })
        .catch((error) => {
          console.error(
            "Error al eliminar los registros de control de aves:",
            error
          );
        });
    }
  };

  const handleFileUpload = async (file) => {
    if (!(file instanceof Blob)) {
      alert(
        "El archivo seleccionado no es válido. Por favor, selecciona un archivo Excel."
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);

      // Validar el archivo antes de procesar
      const validationResult = validateFile(jsonData);
      if (!validationResult.isValid) {
        alert(`Error en el archivo: ${validationResult.message}`);
        return;
      }

      try {
        setLoading(true);
        for (const record of jsonData) {
          const {
            Lote,
            Fecha,
            "N° Aves Muertas": nAvesMuertas,
            "Motivo de la Muerte": motivoMuerte,
            GranjaId,
          } = record;

          // Convertir la fecha desde el formato Excel a ISO y ajustar el día
          const dateFromExcel = new Date((Fecha - (25567 + 2)) * 86400 * 1000);
          dateFromExcel.setDate(dateFromExcel.getDate() + 1); // Sumar un día
          const isoDate = dateFromExcel.toISOString().split("T")[0];

          // Manejar el caso donde motivoMuerte podría estar vacío o no definido
          const motivos = motivoMuerte
            ? motivoMuerte.split(",").map((motivo) => motivo.trim())
            : Array(nAvesMuertas).fill(""); // Rellenar con cadenas vacías si no hay motivos

          if (nAvesMuertas === 0) {
            // Si no hay aves muertas, crear un registro con death = 0
            const controlData = {
              fecha: isoDate,
              ave_id: Lote,
              granjaId: GranjaId,
              motivo_muerte: "", // Sin motivo de muerte
              death: false,
            };

            await axios.post(
              `${process.env.REACT_APP_API_URL}/control-aves`,
              controlData
            );
          } else if (nAvesMuertas > 0) {
            // Si hay aves muertas, crear registros con death = 1
            for (let i = 0; i < nAvesMuertas; i++) {
              const controlData = {
                fecha: isoDate, // Usar la fecha en formato ISO
                ave_id: Lote,
                granjaId: GranjaId,
                motivo_muerte: motivos[i] || "", // Asignar motivo correspondiente
                death: true,
              };

              await axios.post(
                `${process.env.REACT_APP_API_URL}/control-aves`,
                controlData
              );
            }
          }
        }
        setLoading(false);
        fetchControlAves(); // Refrescar la lista de controles después de la importación
        setDropzone(false);
        setSelectedFile(null);
        alert("Datos importados exitosamente.");
      } catch (error) {
        setLoading(false);
        console.error("Error al cargar datos desde el archivo Excel:", error);
        alert(
          "Ocurrió un error al cargar los datos. Por favor, intenta nuevamente."
        );
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const validateFile = (data) => {
    const requiredColumns = [
      "Lote",
      "Fecha",
      "N° Aves Muertas",
      "Motivo de la Muerte", // Esta columna puede tener valores vacíos
      "GranjaId",
    ];

    // Verificar que todas las columnas necesarias estén presentes
    const columns = Object.keys(data[0]);
    for (const col of requiredColumns) {
      if (!columns.includes(col)) {
        return {
          isValid: false,
          message: `Falta la columna requerida: ${col}`,
        };
      }
    }

    // Validar cada fila
    for (let i = 0; i < data.length; i++) {
      const row = data[i];

      // Verificar que la fecha es válida
      if (isNaN(new Date(row.Fecha))) {
        return {
          isValid: false,
          message: `La fecha en la fila ${i + 1} es inválida: ${row.Fecha}`,
        };
      }

      // Verificar que el número de aves muertas es un número válido
      if (isNaN(row["N° Aves Muertas"])) {
        return {
          isValid: false,
          message: `El campo "N° Aves Muertas" en la fila ${
            i + 1
          } contiene un valor no numérico: ${row["N° Aves Muertas"]}`,
        };
      }

      // Manejar el caso donde la columna "Motivo de la Muerte" podría estar vacía o no definida
      let motivos = row["Motivo de la Muerte"]
        ? row["Motivo de la Muerte"].split(",").map((motivo) => motivo.trim())
        : [];

      const numAvesMuertas = parseInt(row["N° Aves Muertas"], 10);

      // Si la cantidad de motivos es menor que el número de aves muertas, completar con "Desconocido"
      if (motivos.length < numAvesMuertas) {
        const diferencia = numAvesMuertas - motivos.length;
        motivos = [...motivos, ...Array(diferencia).fill("Desconocido")];
        row["Motivo de la Muerte"] = motivos.join(","); // Actualizar la fila con los motivos completos
      }

      // Verificar que el GranjaId es un número válido
      if (isNaN(row.GranjaId)) {
        return {
          isValid: false,
          message: `El campo "GranjaId" en la fila ${
            i + 1
          } contiene un valor no numérico: ${row.GranjaId}`,
        };
      }
    }

    // Si todo está bien
    return {
      isValid: true,
      message: "El archivo es válido",
    };
  };

  const downloadTemplate = () => {
    const workbook = XLSX.utils.book_new();

    const dataTemplate = [
      {
        Lote: 1, // Ejemplo de ID de Lote
        Fecha: "dd/MM/yyyy",
        "N° Aves Muertas": 3,
        "Motivo de la Muerte": "Enfermedad, Picaje, Asfixia",
        GranjaId: 1, // Ejemplo de ID de Granja
      },
    ];

    const worksheetTemplate = XLSX.utils.json_to_sheet(dataTemplate);
    XLSX.utils.book_append_sheet(workbook, worksheetTemplate, "Template");
    XLSX.writeFile(workbook, "control_aves_template.xlsx");
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    // Ordenar por fecha de más reciente a más antiguo
    const sortedData = [...filteredControlAves].sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha)
    );

    const dataToExport = sortedData.map((control) => ({
      Fecha: renderDateTitle(control.fecha),
      Lote: getAveNombre(control.lote),
      "Total de Aves Muertas": control.totalMuertas,
      "Motivos de Muerte": control.motivos.join(", "),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Control de Aves");

    XLSX.writeFile(workbook, "control_aves.xlsx");
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  return (
    <>
      <div className="flex">
        <button
          onClick={() => setShowAddControlAves(true)}
          className="btn btn-green control-action-btn"
        >
          Reportar Mortalidad <FontAwesomeIcon icon={faSkull} />
        </button>
      </div>

      {paginatedData.length === 0 && (
        <div className="d-flex">
          <button className="btn btn-warning me-2" onClick={setDropzone}>
            <FontAwesomeIcon icon={faFileImport} /> Import{" "}
          </button>
        </div>
      )}
      {paginatedData.length === 0 ? (
        <></>
      ) : (
        <div className="control-secondary-btns">
          <button className="btn btn-warning" onClick={setDropzone}>
            <FontAwesomeIcon icon={faFileImport} /> Import{" "}
          </button>
          <button className="btn btn-success" onClick={exportToExcel}>
            <FontAwesomeIcon icon={faFileExcel} /> Export{" "}
          </button>
          <button onClick={handleDeleteAll} className="btn btn-danger">
            <FontAwesomeIcon icon={faTrash} /> All
          </button>
        </div>
      )}

      {dropzone && (
        <div className="dropzone-container">
          {/* Botón para cerrar el dropzone */}
          <button className="close-icon" onClick={() => setDropzone(false)}>
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {/* Área de dropzone con instrucciones */}
          <div
            className={`dropzone ${dragging ? "bg-secondary text-white" : ""}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <h5>Importar Datos</h5>
            <p>
              {dragging
                ? "Suelta tu archivo aquí..."
                : "Arrastra y suelta un archivo Excel aquí, o haz clic en el botón para seleccionar un archivo."}
            </p>
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileSelect}
              style={{ display: "none" }}
              id="file-input"
            />
            <label htmlFor="file-input" className=" btn-outline-primary">
              Selecciona un archivo <FontAwesomeIcon icon={faFileImport} />
            </label>
            {selectedFile && (
              <div className="mt-3">
                <p>
                  <strong>Archivo seleccionado:</strong> {selectedFile.name}
                </p>
                <button
                  className="btn btn-success"
                  onClick={() => handleFileUpload(selectedFile)}
                >
                  Importar archivo <FontAwesomeIcon icon={faFileImport} />
                </button>
              </div>
            )}
          </div>

          {/* Opción para descargar la plantilla */}
          <p
            onClick={downloadTemplate}
            className="text-primary mt-2"
            style={{ cursor: "pointer" }}
          >
            <FontAwesomeIcon icon={faFileImport} /> Descargar plantilla
          </p>
        </div>
      )}

      {paginatedData.length === 0 ? (
        <div className="text-center color-white my-4">
          <p className="fw-bold">
            ¿Aún no llevas el registro diario de tus aves?
          </p>
          <p>
            ¡Empieza a monitorear su salud, controlar la mortalidad y optimizar
            el rendimiento de tu granja hoy mismo!
          </p>
        </div>
      ) : (
        <>
          <div className="control-cards">
            {paginatedData.map((control) => (
              <div
                className="control-card"
                key={`${control.fecha}_${control.lote}`}
              >
                <div className="card-body">
                  <h5>{getAveNombre(control.lote)}</h5>
                  <p>
                    <strong>Fecha:</strong> {renderDateTitle(control.fecha)}
                  </p>
                  <p>
                    <strong>Aves perdidas:</strong>{" "}
                    {control.totalMuertas > 0
                      ? control.totalMuertas
                      : "No hubo mortalidad"}
                  </p>
                  {control.totalMuertas > 0 && (
                    <p>
                      <strong>Motivos de Muerte:</strong>{" "}
                      {control.motivos.join(", ")}
                    </p>
                  )}
                </div>
                <div className="card-footer">
                  <button
                    className="btn btn-warning me-2"
                    onClick={() =>
                      handleEditControlAve(control.fecha, control.lote)
                    }
                  >
                    <FontAwesomeIcon icon={faEdit} /> Editar
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => handleDelete(control.fecha, control.lote)}
                  >
                    <FontAwesomeIcon icon={faTrash} /> Eliminar
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="flex">
            {startPage > 1 && (
              <button className="" onClick={handlePrevPageGroup}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            )}
            {renderPagination()}
            {startPage + pagesToShow - 1 < totalPages && (
              <button className="" onClick={handleNextPageGroup}>
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default ControlAves;
